import {
  HistoryFromField,
  defaultHistoryFrom,
  historyFromPayload,
  historyNote,
} from "@/components/analysis/history-from-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { Link, Navigate, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/agents/analyst/whatsapp` — admin-only. The list of paired (watched) chats; each
 * opens its full transcript. Top-right: "Pair new chat" and the WhatsApp
 * "Account" (link / unlink, QR) — both in modals.
 *
 * E2E behaviour contract lives in `e2e/analysis-whatsapp.spec.ts`.
 */

type Project = { id: string; name: string };
type PickerChat = { chatId: string; name: string; messageCount: number; enabled: boolean };
type PairedChat = {
  chatId: string;
  name: string;
  projectId: string | null;
  projectName: string | null;
  historyFrom: string | null;
  messageCount: number;
  processedCount: number;
  /** Messages kept only as background for the AI (older than historyFrom). */
  contextCount: number;
};
type WhatsAppStatus = {
  enabled: boolean;
  status: "disconnected" | "connecting" | "qr" | "connected";
  qr: string | null;
  me: { id: string; name?: string } | null;
  error: string | null;
  /** Outcome of the last "Load full history". History is answered by the
   *  paired phone and often doesn't come — the button must say which it was. */
  lastHistoryPull: {
    at: string;
    chats: number;
    brought: number;
    running: boolean;
  } | null;
};

export function WhatsAppPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [projects, setProjects] = useState<Project[]>([]);
  const [wa, setWa] = useState<WhatsAppStatus | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [paired, setPaired] = useState<PairedChat[]>([]);
  const [picker, setPicker] = useState<PickerChat[]>([]);
  const [historyFrom, setHistoryFrom] = useState(defaultHistoryFrom());

  const isAdmin = user.globalRole === "admin";

  const loadPaired = useCallback(
    () =>
      api
        .get<{ chats: PairedChat[] }>("/api/auto-sources/paired-chats")
        .then((r) => setPaired(r.chats))
        .catch(() => {}),
    [],
  );
  const loadPicker = useCallback(
    () =>
      api
        .get<{ chats: PickerChat[] }>("/api/auto-sources/whatsapp-chats")
        .then((r) => setPicker(r.chats))
        .catch(() => {}),
    [],
  );
  const refreshWa = useCallback(async (): Promise<WhatsAppStatus | null> => {
    try {
      const s = await api.get<WhatsAppStatus>("/api/analysis/whatsapp/status");
      setWa(s);
      return s;
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        const off: WhatsAppStatus = {
          enabled: false,
          status: "disconnected",
          qr: null,
          me: null,
          error: null,
          lastHistoryPull: null,
        };
        setWa(off);
        return off;
      }
      return null;
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    api
      .get<{ projects: Project[] }>("/api/projects?pageSize=200")
      .then((r) => setProjects(r.projects.map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
    void loadPaired();
  }, [isAdmin, loadPaired]);

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    const tick = async () => {
      const s = await refreshWa();
      if (alive && s?.status === "connected") void loadPaired();
    };
    void tick();
    const id = setInterval(() => void tick(), 5000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [isAdmin, refreshWa, loadPaired]);

  if (!isAdmin) return <Navigate to="/" />;

  async function connectWa() {
    setConnecting(true);
    try {
      setWa(await api.post<WhatsAppStatus>("/api/analysis/whatsapp/connect", {}));
    } catch {
      toast({ title: "Couldn't start WhatsApp pairing.", variant: "destructive" });
    } finally {
      setConnecting(false);
    }
  }
  async function logoutWa() {
    try {
      setWa(await api.post<WhatsAppStatus>("/api/analysis/whatsapp/logout", {}));
      setPaired([]);
    } catch {
      toast({ title: "Couldn't unlink WhatsApp.", variant: "destructive" });
    }
  }
  async function loadHistory() {
    try {
      const res = await api.post<{ chats: number }>("/api/auto-sources/whatsapp-history", {});
      toast({
        title: "Asking the phone for history…",
        description: `${res.chats} paired chat${res.chats === 1 ? "" : "s"}. The result appears above the list — it can come back empty.`,
      });
    } catch {
      toast({ title: "Couldn't start the history load.", variant: "destructive" });
    }
  }
  async function pairChat(chatId: string, name: string) {
    // Send the name we already show in the picker — the group may have no
    // observed messages yet, so the server can't always resolve it itself.
    await api
      .post("/api/auto-sources/paired-chats", {
        chatId,
        name,
        historyFrom: historyFromPayload(historyFrom),
      })
      .catch(() => {});
    await loadPaired();
    await loadPicker();
  }
  /** Move a watched chat's "suggest from" date. Re-pairing the same chat is
   *  an upsert on the server (it re-labels what's stored, keeps it), so this
   *  never goes near the destructive unpair. */
  async function setChatHistoryFrom(chat: PairedChat, historyFromValue: string) {
    try {
      await api.post("/api/auto-sources/paired-chats", {
        chatId: chat.chatId,
        name: chat.name,
        historyFrom: historyFromPayload(historyFromValue),
      });
      await loadPaired();
      toast({
        title: "Date changed.",
        description: "Use Load full history (or re-link) to pull what's missing.",
      });
    } catch {
      toast({ title: "Couldn't change the date.", variant: "destructive" });
    }
  }
  async function unpairChat(chatId: string) {
    await api
      .delete(`/api/auto-sources/paired-chats/${encodeURIComponent(chatId)}`)
      .catch(() => {});
    await loadPaired();
  }
  async function setChatProject(chatId: string, projectId: string | null) {
    setPaired((prev) =>
      prev.map((c) =>
        c.chatId === chatId
          ? { ...c, projectId, projectName: projects.find((p) => p.id === projectId)?.name ?? null }
          : c,
      ),
    );
    await api
      .patch(`/api/auto-sources/paired-chats/${encodeURIComponent(chatId)}`, { projectId })
      .catch(() => void loadPaired());
  }

  const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));
  const pairedIds = new Set(paired.map((c) => c.chatId));
  const pickable = picker.filter((c) => !c.enabled && !pairedIds.has(c.chatId));
  const connected = wa?.status === "connected";

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">WhatsApp</h2>
          <p className="text-muted-foreground mt-1">
            Watched chats are read continuously; open one to see its transcript and what it led to.
          </p>
        </div>
        <div className="flex gap-2">
          {connected && (
            <Dialog onOpenChange={(o) => o && loadPicker()}>
              <DialogTrigger asChild>
                <Button type="button" size="sm" data-testid="pair-new-open">
                  Pair new chat
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Pair a chat</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                  Groups your linked account is in (most recently active first). Pairing starts
                  watching the chat.
                </p>
                <HistoryFromField value={historyFrom} onChange={setHistoryFrom} />
                {pickable.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No more recent groups to pair.</p>
                ) : (
                  <ul className="max-h-80 divide-y overflow-auto rounded-md border">
                    {pickable.map((ch) => (
                      <li
                        key={ch.chatId}
                        className="flex items-center justify-between gap-3 px-3 py-2"
                        data-testid="pair-candidate"
                      >
                        <span className="text-sm">
                          <span className="font-medium">{ch.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {" "}
                            ({ch.messageCount})
                          </span>
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => pairChat(ch.chatId, ch.name)}
                        >
                          Pair
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </DialogContent>
            </Dialog>
          )}
          {/* On the page, not inside the account modal: this is what someone
              reaches for when a paired chat came out empty, and hunting for it
              behind another button is the last thing that helps then. */}
          {connected && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={loadHistory}
              data-testid="load-history"
            >
              Load full history
            </Button>
          )}
          <WhatsAppAccountModal
            wa={wa}
            connecting={connecting}
            onConnect={connectWa}
            onLogout={logoutWa}
          />
        </div>
      </div>

      {/* The history comes from the phone, so "Load full history" can finish
          having brought nothing at all. Saying so — and saying what does work
          instead — beats a toast that promises a background job and then
          leaves the chats exactly as empty as before. */}
      {connected && wa?.lastHistoryPull && (
        <p
          className={
            wa.lastHistoryPull.running || wa.lastHistoryPull.brought > 0
              ? "text-sm text-muted-foreground"
              : "text-sm text-amber-600 dark:text-amber-500"
          }
          data-testid="history-pull-result"
        >
          {wa.lastHistoryPull.running ? (
            <>Pulling history from the phone for {wa.lastHistoryPull.chats} chat(s)…</>
          ) : wa.lastHistoryPull.brought > 0 ? (
            <>Last history pull brought {wa.lastHistoryPull.brought} message(s).</>
          ) : (
            <>
              Last history pull brought nothing — the phone answered with no older messages. The
              dependable way to fill a chat is <strong>Account → Unlink</strong> and link again: on
              pairing the phone pushes its whole history.
            </>
          )}
        </p>
      )}

      {!connected ? (
        <p className="text-sm text-muted-foreground" data-testid="whatsapp-not-connected">
          WhatsApp isn't connected. Use <strong>Account</strong> (top right) to link it.
        </p>
      ) : paired.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="paired-empty">
          No paired chats yet. Use <strong>Pair new chat</strong> to start watching a group.
        </p>
      ) : (
        <ul className="divide-y rounded-md border">
          {paired.map((ch) => (
            <li
              key={ch.chatId}
              className="flex flex-wrap items-center justify-between gap-3 px-3 py-2"
              data-testid="paired-chat-row"
            >
              <div className="min-w-0 flex-1">
                <Link
                  to="/analysis/chats/$chatId"
                  params={{ chatId: ch.chatId }}
                  className="font-medium underline"
                  data-testid="paired-chat-open"
                >
                  {ch.name}
                </Link>
                <span className="ml-2 text-xs text-muted-foreground">
                  {ch.processedCount}/{ch.messageCount} processed
                </span>
                <span
                  className="ml-2 text-xs text-muted-foreground"
                  data-testid="history-from-note"
                >
                  {historyNote(ch.historyFrom, ch.contextCount)}
                </span>
                {/* Changing the date must not mean unpair + pair again: the
                    messages hang off the pairing row by a cascading foreign
                    key, so unpairing throws the whole stored chat away. */}
                <ChangeHistoryFromDialog chat={ch} onSave={setChatHistoryFrom} />
              </div>
              <div className="flex items-center gap-2">
                <Select
                  className="w-44"
                  value={ch.projectId ?? ""}
                  onChange={(v) => setChatProject(ch.chatId, v || null)}
                  aria-label={`Project for ${ch.name}`}
                  options={[{ value: "", label: "— no project —" }, ...projectOptions]}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => unpairChat(ch.chatId)}
                  data-testid="unpair-chat"
                >
                  Unpair
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Change where a watched chat's suggestions start, in place. "no history
 * loaded" means the chat was paired without a date and no history is ever
 * asked for — and the only way to fix that used to be unpairing, which
 * cascades the stored messages away with the pairing.
 */
function ChangeHistoryFromDialog({
  chat,
  onSave,
}: {
  chat: PairedChat;
  onSave: (chat: PairedChat, historyFrom: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(chat.historyFrom?.slice(0, 10) ?? defaultHistoryFrom());

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="link"
          size="sm"
          className="ml-1 h-auto p-0 text-xs"
          data-testid="change-history-from"
        >
          change
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Suggestions from — {chat.name}</DialogTitle>
        </DialogHeader>
        <HistoryFromField value={value} onChange={setValue} />
        <p className="text-sm text-muted-foreground">
          Only re-labels the chat: stored messages stay, whatever you pick. Nothing is unpaired.
        </p>
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            data-testid="save-history-from"
            onClick={async () => {
              await onSave(chat, value);
              setOpen(false);
            }}
          >
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The link / unlink (+ QR) flow, in a modal opened by the "Account" button. */
function WhatsAppAccountModal({
  wa,
  connecting,
  onConnect,
  onLogout,
}: {
  wa: WhatsAppStatus | null;
  connecting: boolean;
  onConnect: () => void;
  onLogout: () => void;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid="whatsapp-account-open">
          Account
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>WhatsApp account</DialogTitle>
        </DialogHeader>
        {wa === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !wa.enabled ? (
          <p className="text-sm text-muted-foreground" data-testid="whatsapp-off">
            WhatsApp is not enabled on the server. Set <code>WHATSAPP_ENABLED=true</code>.
          </p>
        ) : wa.status === "connected" ? (
          <div className="space-y-3" data-testid="whatsapp-connected">
            <p className="text-sm">
              Linked as <span className="font-medium">{wa.me?.name ?? wa.me?.id ?? "—"}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              Re-linking makes the phone push its whole history again — the way to fill chats that
              were paired while the account was already linked.
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onLogout}>
                Unlink
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="whatsapp-pairing">
            {wa.status === "qr" && wa.qr ? (
              <div className="space-y-2">
                <p className="text-sm">
                  Open WhatsApp → <strong>Settings → Linked devices → Link a device</strong> and
                  scan:
                </p>
                <img
                  src={wa.qr}
                  alt="WhatsApp pairing QR code"
                  data-testid="whatsapp-qr"
                  className="h-64 w-64 rounded-md border bg-white p-2"
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {wa.status === "connecting"
                  ? "Connecting to WhatsApp…"
                  : "Link a WhatsApp account to watch chats."}
              </p>
            )}
            {wa.error && <p className="text-xs text-destructive">{wa.error}</p>}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onConnect}
              disabled={connecting || wa.status === "connecting" || wa.status === "qr"}
              data-testid="whatsapp-connect"
            >
              {wa.status === "qr" || wa.status === "connecting"
                ? "Waiting for scan…"
                : connecting
                  ? "Starting…"
                  : "Link WhatsApp"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
